import { isRosterLikeSheet, type ExcelParseResult, type ParseNotice } from '@shared/column-matching'
import { getFileExt } from '@shared/types'
import { parseCsvToExcelResult } from './parseCsvToExcelResult'

export type RosterIssue = ParseNotice & { fileName: string }

export interface RosterCheck {
  result: ExcelParseResult | null
  errors: RosterIssue[]
}

// A skipped sheet whose headers look like roster columns would drop people from the
// headcount, so it blocks. Any other skipped sheet (Notes, Legend, Lookup, empty) stays
// a warning: the rest of the file is processed and that sheet is left out.
function skippedSheetErrors(result: ExcelParseResult, fileName: string): RosterIssue[] {
  return result.warnings
    .filter((w) => w.code === 'sheet-skipped' && isRosterLikeSheet(w.detectedHeaders ?? []))
    .map((w) => ({
      ...w,
      fileName,
      message:
        `Sheet "${w.sheet}" looks like roster data but cannot be read, so its rows would be left out of the headcount. ` +
        `It needs a Role column and a Subfunction (or Task/Description) column in the first 30 rows. ` +
        `Headers found: ${(w.detectedHeaders ?? []).slice(0, 12).join(', ')}. ` +
        blankHeaderNote(w.blankHeaderCount ?? 0) +
        'Rename the columns, or delete the sheet if it is not part of the roster, then re-upload.',
    }))
}

// The missing Role or Subfunction data may sit under an unnamed column, so keep the parser's hint.
function blankHeaderNote(count: number): string {
  if (count === 0) return ''
  return `${count} column${count > 1 ? 's have' : ' has'} a blank header — if the missing data is there, add a header name (e.g. "Role"). `
}

/** The blocking check every roster passes before the pipeline. */
export async function checkRosterParse(
  fileName: string,
  parse: () => Promise<ExcelParseResult>
): Promise<RosterCheck> {
  let result: ExcelParseResult
  try {
    result = await parse()
  } catch (err) {
    return {
      result: null,
      errors: [{ code: 'no-data-rows', fileName, message: err instanceof Error ? err.message : String(err) }],
    }
  }

  const errors: RosterIssue[] = result.errors.map((n) => ({ ...n, fileName }))
  if (errors.length === 0 && result.data.subfunctions.length === 0) {
    errors.push({ code: 'no-data-rows', fileName, message: 'No roles could be read from this file.' })
  }
  errors.push(...skippedSheetErrors(result, fileName))
  return { result, errors }
}

/** Validate a base64 roster (XLSX or CSV); any other file type has nothing to check. */
export async function validateRosterFile(
  file: { name: string; base64: string },
  parseExcel: (base64: string) => Promise<ExcelParseResult>
): Promise<RosterCheck> {
  const ext = getFileExt(file.name)
  if (ext === '.xlsx') return checkRosterParse(file.name, () => parseExcel(file.base64))
  if (ext === '.csv') {
    return checkRosterParse(file.name, async () => {
      const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0))
      return parseCsvToExcelResult(new TextDecoder().decode(bytes))
    })
  }
  return { result: null, errors: [] }
}
